import { useQuery } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { partnerApi, type Partner } from '../lib/api';
import type { LaunchDarklyFlags } from '../types/launch-darkly';
import { Sidebar } from './Sidebar';
import { appRoutes } from '../app-routes';

interface LayoutProps {
  children: React.ReactNode;
}

/**
 * Layout Component
 * Provides persistent top navigation bar across all pages
 */
export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const flags = useFlags<LaunchDarklyFlags>();

  const { data: partner } = useQuery<Partner>({
    queryKey: ['partner'],
    queryFn: () => partnerApi.getPartner(),
  });

  const enablePartnerAutomationEditor =
    flags['partner-automation-editor'] ?? false;
  const engagePath = enablePartnerAutomationEditor
    ? `${appRoutes.ENGAGE_SETUP}/settings`
    : appRoutes.ENGAGE_SETUP;

  // Determine active sidebar item based on route
  const getActiveSidebarItem = () => {
    if (location.pathname.startsWith('/dashboard/metrics')) return 'metrics';
    if (location.pathname.startsWith('/dashboard/tenants')) return 'tenants';
    if (location.pathname.startsWith('/dashboard/users')) return 'users';
    if (location.pathname.startsWith('/dashboard/communications'))
      return 'communications';
    if (location.pathname.startsWith(appRoutes.ENGAGE_SETUP)) return 'engage';
    if (location.pathname === '/dashboard/settings') return 'profile';
    if (location.pathname === '/dashboard/settings/api') return 'api-keys';
    if (location.pathname === '/dashboard/settings/theme') return 'branding';
    if (location.pathname.startsWith('/dashboard/settings/data-integration'))
      return 'data-integration';
    return 'metrics'; // default to metrics dashboard
  };

  const handleSidebarItemSelect = (itemId: string) => {
    switch (itemId) {
      case 'metrics':
        navigate('/dashboard/metrics');
        break;
      case 'profile':
        navigate('/dashboard/settings');
        break;
      case 'tenants':
        navigate('/dashboard/tenants');
        break;
      case 'users':
        navigate('/dashboard/users');
        break;
      case 'engage':
        navigate(engagePath);
        break;
      default:
        navigate('/dashboard/coming-soon');
        break;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Main Layout with Sidebar */}
      <div className="flex h-screen">
        {/* Sidebar */}
        <Sidebar
          activeItem={getActiveSidebarItem()}
          onItemSelect={handleSidebarItemSelect}
          partner={partner}
        />

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          <div className="w-full max-w-[80vw] mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};
